import React, { useMemo, useCallback, useEffect, useState } from "react";
import { observer } from "mobx-react-lite";
import { useHistory } from "react-router-dom";
import { FormikHelpers, FormikValues } from "formik";

import "../../Project.scss";
import ProjectInput, { CustomPropertyValue } from "../../../../services/project/ProjectInput";
import { dateService } from "../../../../services/date-service/DateService";
import { ProjectDynamicMenuItemsPages, ProjectMenuItemsModel } from "../../ProjectMenuItems";
import {
  DataSection,
  SubSection,
  EditDescriptionSection,
} from "../../../../components/data-components/sections/DataSections";
import {
  EditProjectName,
  EditProjectLead,
  EditProjectStatus,
} from "../../components/DataComponents";
import DisplayDateCreated from "../../../../components/data-components/DisplayDateCreated";
import DisplayCreatedBy from "../../../../components/data-components/DisplayCreatedBy";
import {
  EditProjectAssignedTo,
  EditProject as EditProjectPermission,
  EditProjectLead as EditProjectLeadPermission,
} from "../../../../services/authorization/Permissions";
import PageLayout from "../../../_layout/PageLayout";
import Form from "../../../../components/form/Form";
import useErrorHandler, {
  ApplicationError,
} from "../../../../services/error-handler/ErrorHandlerService";
import { EditCoordinateSystem } from "../../../settings/gis-coordinates/components/DataComponents";
import { EditAvailableFromAndDeadlineDates } from "../../components/DataComponents";
import { EditProjectFormat } from "../../components/EditProjectFormat";
import { EditProjectCodeSystem } from "../../components/EditProjectCodeSystem";
import { ProjectFormat } from "../../../../services/project/ProjectFormat";
import { ProjectCodeSystem } from "../../../../services/project/ProjectCodeSystem";
import { CurrentProjectStorageModel } from "../../../../services/project/CurrentProjectStorage";
import { EditCustomPropertiesSection } from "./components/DataComponents";
import { api } from "../../../../services/api/ApiService";
import useUrlParams from "../../../../services/url/UrlParamsService";
import { ProjectFormatCodeSystemServiceModel } from "../../../../services/project/ProjectFormatCodeSystemService";
import { EditReportTemplate, emptyReportTemplate } from "../../components/EditReportTemplate";
import { featureFlagsStorage } from "../../../../services/feature-flags/FeatureFlagsStorage";
import { warningLeavePageService } from "../../../../services/page/WarningLeavePageService";
import { permissionsService } from "../../../../services/authorization/UserPermissionsService";
import { breadcrumbsStorage } from "../../../../services/breadcrumbs/BreadcrumbsStorage";
import { EntityTypeFromPath } from "../../../../services/breadcrumbs/EntityTypeFromPathModel";
import { ProjectStatus } from "../../../../generated/Projects";
import { Flags } from "../../../../generated/FeatureFlags";

export default observer(() => {
  const history = useHistory();
  const urlParamsService = useUrlParams();
  const { handleErrorInSnackbar } = useErrorHandler();

  const currentProjectId = urlParamsService.getProjectId()!;

  const [currentProjectStorage] = useState(() =>
    CurrentProjectStorageModel.create({ projectId: currentProjectId }, { api })
  );

  const [projectFormatCodeSystemService] = useState(() =>
    ProjectFormatCodeSystemServiceModel.create({}, { api })
  );

  const menuItems = useMemo(() => {
    const projectMenuItems = ProjectMenuItemsModel.create({
      projectId: currentProjectId,
      isLoading: currentProjectStorage.isLoading,
      editMode: true,
      dynamicMenuItemPage: ProjectDynamicMenuItemsPages.EditProject,
      numberOfSewerObjects: currentProjectStorage.currentProject.sewerObjectsCount,
    });

    return {
      staticItems: projectMenuItems.getStaticItems(),
    };
  }, [
    currentProjectId,
    currentProjectStorage.currentProject.sewerObjectsCount,
    currentProjectStorage.isLoading,
  ]);

  const isPageLoading = useMemo(
    () =>
      !currentProjectStorage.currentProject ||
      !currentProjectStorage.currentProject.id ||
      projectFormatCodeSystemService.formatsAreLoading ||
      projectFormatCodeSystemService.codeSystemsAreLoading,
    [
      currentProjectStorage.currentProject,
      projectFormatCodeSystemService.codeSystemsAreLoading,
      projectFormatCodeSystemService.formatsAreLoading,
    ]
  );

  const customPropertiesInitialValues = useMemo(() => {
    return currentProjectStorage.getCustomPropertiesInitialValues();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentProjectStorage.currentProject.customPropertyValues]);

  const initialValues = useMemo(() => {
    const gis = currentProjectStorage.currentProject.gisCoordinates;

    const gisName = { id: gis.id, name: gis.name };
    const gisWkid = { id: gis.id, name: gis.wkid };

    return {
      id: currentProjectStorage.currentProject.id,
      name: currentProjectStorage.currentProject.name,
      status: {
        id: currentProjectStorage.currentProject.status,
        name: ProjectStatus[currentProjectStorage.currentProject.status],
      },
      leadId: currentProjectStorage.currentProject.lead.id,
      lead: {
        id: currentProjectStorage.currentProject.lead.id,
        name: currentProjectStorage.currentProject.lead.name,
      },
      availableFrom: new Date(currentProjectStorage.currentProject.availableFrom),
      deadline: currentProjectStorage.currentProject.deadline
        ? new Date(currentProjectStorage.currentProject.deadline)
        : null,
      description: currentProjectStorage.currentProject.description,
      gisCoordinatesId: gis.id,
      coordinateSystemName: gisName,
      coordinateSystemWKID: gisWkid,
      formatId: projectFormatCodeSystemService.selectedProjectFormat
        ? projectFormatCodeSystemService.selectedProjectFormat.id
        : "",
      format: projectFormatCodeSystemService.selectedProjectFormat,
      customPropertyValues: customPropertiesInitialValues,
      codeSystemId: projectFormatCodeSystemService.selectedProjectCodeSystem
        ? projectFormatCodeSystemService.selectedProjectCodeSystem.id
        : "",
      codeSystem: projectFormatCodeSystemService.selectedProjectCodeSystem,
      reportTemplate: currentProjectStorage.currentProject.reportTemplate
        ? {
            id: currentProjectStorage.currentProject.reportTemplate.id,
            name: currentProjectStorage.currentProject.reportTemplate.name,
          }
        : emptyReportTemplate,
    };
  }, [
    currentProjectStorage.currentProject,
    projectFormatCodeSystemService.selectedProjectFormat,
    projectFormatCodeSystemService.selectedProjectCodeSystem,
    customPropertiesInitialValues,
  ]);

  const goToProjectPage = useCallback(
    () => history.push(`/projects/${currentProjectStorage.currentProject.id}`),
    [currentProjectStorage.currentProject.id, history]
  );
  const isEditCodeSystemDisabled = useMemo(
    () =>
      projectFormatCodeSystemService.codeSystemDisabled ||
      projectFormatCodeSystemService.selectedProjectFormat.readonly,
    [
      projectFormatCodeSystemService.codeSystemDisabled,
      projectFormatCodeSystemService.selectedProjectFormat.readonly,
    ]
  );

  const handleSubmit = async (values: FormikValues, formActions: FormikHelpers<FormikValues>) => {
    const availableFrom = dateService.dateToUTC(values.availableFrom);

    let deadline: any = null;
    if (values.deadline !== null) {
      deadline = dateService.dateToUTC(values.deadline);
    }

    const customPropertyValuesList: CustomPropertyValue[] = [];

    Object.entries(values.customPropertyValues).forEach((entry) => {
      var [label, value] = entry;
      const property = {
        label,
        value: value as string,
      };
      customPropertyValuesList.push(property);
    });

    const projectToEdit: ProjectInput = {
      id: currentProjectStorage.currentProject.id,
      name: values.name,
      status: Number(values.status.id),
      leadId: values.leadId,
      timeline: {
        availableFrom: availableFrom,
        deadline: deadline,
      },
      description: values.description,
      gisCoordinatesId: values.gisCoordinatesId,
      formatId: projectFormatCodeSystemService.selectedProjectFormat.id,
      codeSystemId: projectFormatCodeSystemService.selectedProjectCodeSystem.id,
      customPropertyValues: customPropertyValuesList,
      reportTemplateId: values.reportTemplate?.id !== 0 ? values.reportTemplate?.id : undefined,
    };
    try {
      await currentProjectStorage.editProject(projectToEdit);
      warningLeavePageService.setIsDirty(false);
      return goToProjectPage();
    } catch (error) {
      const applicationError = error as ApplicationError;
      applicationError.withForm(values, formActions);
      applicationError.withErrorMessage("default-error.edit");
      handleErrorInSnackbar(applicationError);
    }
  };

  useEffect(() => {
    async function setupProjectFormatAndCodeSystemInitialData() {
      await projectFormatCodeSystemService.setupEditProjectFormatAndCodeSystemInitialData(
        currentProjectStorage.currentProject.format,
        currentProjectStorage.currentProject.codeSystem
      );
    }

    if (!currentProjectStorage.isLoading) setupProjectFormatAndCodeSystemInitialData();
  }, [
    currentProjectStorage.currentProject.codeSystem,
    currentProjectStorage.currentProject.format,
    currentProjectStorage.isLoading,
    projectFormatCodeSystemService,
  ]);

  useEffect(() => {
    async function renewCodeSystems() {
      if (
        projectFormatCodeSystemService.newCodeSystemsRequired &&
        projectFormatCodeSystemService.selectedProjectFormat.isValid
      ) {
        projectFormatCodeSystemService.renewCodeSystems(
          projectFormatCodeSystemService.selectedProjectFormat.id
        );
      }
    }
    renewCodeSystems();
  }, [
    projectFormatCodeSystemService,
    projectFormatCodeSystemService.newCodeSystemsRequired,
    projectFormatCodeSystemService.selectedProjectFormat.id,
    projectFormatCodeSystemService.selectedProjectFormat.isValid,
  ]);

  useEffect(() => {
    breadcrumbsStorage.setEntityNames([
      {
        entity: EntityTypeFromPath.Project,
        name: `${currentProjectStorage.currentProject.name}`,
      },
    ]);

    breadcrumbsStorage.updateBreadcrumbs(history.location.pathname);
  }, [currentProjectStorage.currentProject.name, history.location.pathname]);

  return (
    <PageLayout
      accessWithOneOfThePermissions={[EditProjectPermission, EditProjectAssignedTo]}
      titleKey="EditProject.title"
      titleContext={{ projectName: currentProjectStorage.currentProject.name }}
      id="edit-project"
      menuItems={menuItems}
      isLoading={isPageLoading}
    >
      <Form
        formikOptions={{
          initialValues: initialValues,
          onSubmit: handleSubmit,
          validateOnBlur: false,
          validateOnChange: false,
        }}
        onCancel={goToProjectPage}
      >
        {({ values, setFieldValue }) => {
          return (
            <>
              <DataSection labelKey="general.projects.generic-details">
                <SubSection>
                  <EditProjectName name={values.name} />
                  <EditProjectStatus status={values.status} setFieldValue={setFieldValue} />
                  <EditProjectLead
                    lead={values.lead}
                    leadId={values.leadId}
                    setFieldValue={setFieldValue}
                    disabled={!permissionsService.userHasPermissionTo(EditProjectLeadPermission)}
                  />
                </SubSection>
                <SubSection>
                  <EditProjectFormat
                    value={projectFormatCodeSystemService.selectedProjectFormat}
                    formats={projectFormatCodeSystemService.projectFormats as ProjectFormat[]}
                    disabled={projectFormatCodeSystemService.selectedProjectFormat.readonly}
                    onChange={(newProjectFormat: ProjectFormat) =>
                      projectFormatCodeSystemService.setSelectedProjectFormat(newProjectFormat)
                    }
                    setFieldValue={setFieldValue}
                  />
                  <EditProjectCodeSystem
                    value={projectFormatCodeSystemService.selectedProjectCodeSystem}
                    codeSystems={projectFormatCodeSystemService.codeSystems as ProjectCodeSystem[]}
                    disabled={isEditCodeSystemDisabled}
                    onChange={(newProjectCodeSystem: ProjectCodeSystem) =>
                      projectFormatCodeSystemService.setSelectedProjectCodeSystem(
                        newProjectCodeSystem
                      )
                    }
                    setFieldValue={setFieldValue}
                  />
                  <EditCoordinateSystem
                    name={values.coordinateSystemName}
                    WKID={values.coordinateSystemWKID}
                    setFieldValue={setFieldValue}
                    areFieldsRequired={true}
                  />
                </SubSection>
                <SubSection>
                  <EditAvailableFromAndDeadlineDates
                    availableFrom={values.availableFrom}
                    deadline={values.deadline}
                    setFieldValue={setFieldValue}
                  />
                  {featureFlagsStorage.isFeatureEnabled(
                    Flags.Use_Custom_Report_Template_For_Project
                  ) && (
                    <EditReportTemplate
                      value={values.reportTemplate}
                      setFieldValue={setFieldValue}
                    />
                  )}
                </SubSection>
                <SubSection>
                  <DisplayDateCreated date={currentProjectStorage.currentProject.created} />
                  <DisplayCreatedBy
                    createdBy={currentProjectStorage.currentProject.createdBy.name}
                  />
                </SubSection>
              </DataSection>
              {currentProjectStorage.hasCustomProperties && (
                <EditCustomPropertiesSection
                  labelKey="general.projects.custom-details"
                  properties={currentProjectStorage.currentProject.customPropertyValues}
                  values={values.customPropertyValues}
                  setFieldValue={setFieldValue}
                  nestedFieldsPath="customPropertyValues"
                />
              )}
              <EditDescriptionSection description={values.description} />
            </>
          );
        }}
      </Form>
    </PageLayout>
  );
});
